import { Dialog, Loader } from "@/components";
import { Badge, Button, Input, Label } from "@/components/ui";
import { cn } from "@/lib/utils";
import { RootState } from "@/redux/store";
import {
  useCopyFinancialTemplateMutation,
  useGetFinancialTemplateStructure,
  useGetGlMasterDataDropdown,
  usePublishFinancialTemplateMutation,
  useSaveFinancialTemplateStructureMutation,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowDown, ArrowLeft, ArrowUp, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Copy,
  GripVertical, Plus, Save, Search, Send, Trash2, X,
} from "lucide-react";
import { DragEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";
import { StatusBadge } from "../FinancialStatementVersions";

type Sub = { key: string; name: string; gls: number[] };
type Cat = { key: string; name: string; subs: Sub[] };
type Draft = { title: string; description: string; cats: Cat[] };

let seq = 0;
const newKey = () => `k${Date.now()}-${seq++}`;
const errorMessage = (e: any) => (typeof e?.response?.data?.message === "string" ? e.response.data.message : "Something went wrong");
const move = <T,>(list: T[], from: number, to: number) => {
  if (to < 0 || to >= list.length || from === to) return list;
  const copy = [...list];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
};
const toPayload = (d: Draft) => ({
  title: d.title.trim(),
  description: d.description.trim() || null,
  categories: d.cats.map((c) => ({
    name: c.name.trim(),
    subcategories: c.subs.map((s) => ({ name: s.name.trim(), glMasterIds: s.gls })),
  })),
});

/** Native drag & drop within one list ("scope"); ▲▼ buttons do the same for keyboard / touch. */
const useDrag = () => {
  const dragging = useRef<{ scope: string; index: number } | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const bind = (scope: string, index: number, onMove: (from: number, to: number) => void, enabled: boolean) =>
    enabled
      ? {
          draggable: true,
          onDragStart: (e: DragEvent) => {
            e.stopPropagation();
            dragging.current = { scope, index };
            e.dataTransfer.effectAllowed = "move";
          },
          onDragOver: (e: DragEvent) => {
            if (dragging.current?.scope !== scope) return;
            e.preventDefault();
            e.stopPropagation();
            setOver(`${scope}:${index}`);
          },
          onDragLeave: () => setOver(null),
          onDrop: (e: DragEvent) => {
            if (dragging.current?.scope !== scope) return;
            e.preventDefault();
            e.stopPropagation();
            onMove(dragging.current.index, index);
            dragging.current = null;
            setOver(null);
          },
          onDragEnd: () => {
            dragging.current = null;
            setOver(null);
          },
        }
      : {};
  const isOver = (scope: string, index: number) => over === `${scope}:${index}`;
  return { bind, isOver };
};

const MoveButtons = ({ index, length, onMove, label }: { index: number; length: number; onMove: (to: number) => void; label: string }) => (
  <div className="flex flex-col">
    <button type="button" className="text-gray-400 hover:text-primary disabled:opacity-30" disabled={index === 0}
      onClick={() => onMove(index - 1)} aria-label={`Move ${label} up`}><ArrowUp className="w-3.5 h-3.5" /></button>
    <button type="button" className="text-gray-400 hover:text-primary disabled:opacity-30" disabled={index === length - 1}
      onClick={() => onMove(index + 1)} aria-label={`Move ${label} down`}><ArrowDown className="w-3.5 h-3.5" /></button>
  </div>
);

/** Search-and-add GL picker. GLs already used elsewhere in the template are shown but can't be picked. */
const GlPicker = ({ glOptions, usage, ownKey, onAdd }: {
  glOptions: any[]; usage: Map<number, { subKey: string; where: string }>; ownKey: string; onAdd: (id: number) => void;
}) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    return glOptions
      .filter((g) => !s || `${g.glAccountNumber} ${g.accountShortDesc}`.toLowerCase().includes(s))
      .slice(0, 60);
  }, [glOptions, q]);
  return (
    <div className="relative" ref={ref}>
      <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        placeholder="Search GL number or name to add" className="h-9 bg-white" startIcon={<Search className="w-4 h-4" />} />
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-md border bg-white shadow-lg">
          {results.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-500">No GL accounts match</p>
          ) : results.map((g) => {
            const used = usage.get(g.glMasterId);
            const here = used?.subKey === ownKey;
            return (
              <button key={g.glMasterId} type="button" disabled={!!used}
                onClick={() => { onAdd(g.glMasterId); setQ(""); }}
                className={cn("flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm",
                  used ? "cursor-not-allowed bg-gray-50 text-gray-400" : "hover:bg-primary/5")}>
                <span><span className="font-medium">{g.glAccountNumber}</span> – {g.accountShortDesc}</span>
                {used && <span className="shrink-0 text-xs">{here ? "Added" : `In ${used.where}`}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Financial Statement Version builder (admin). Category > Subcategory > GL accounts; the order at every level
 * (including GLs inside a subcategory) is the order reports show. Save Draft sends the whole structure in one
 * call; Publish makes the version read-only (changes then go into a New Version).
 */
const FinancialVersionBuilder = () => {
  const { financialTemplateId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const community = useSelector((state: RootState) => state.currentData.communityData);
  const { data: structure, isLoading } = useGetFinancialTemplateStructure({ financialTemplateId });
  const template = structure?.template;
  const readOnly = template?.status === "PUBLISHED";
  const { data: glData } = useGetGlMasterDataDropdown({ communityId: template?.communityId ? String(template.communityId) : "" });

  const [draft, setDraft] = useState<Draft>({ title: "", description: "", cats: [] });
  const [saved, setSaved] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<string[]>([]);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [confirm, setConfirm] = useState<null | { title: string; text: string; action: () => void; danger?: boolean }>(null);
  const { bind, isOver } = useDrag();

  useEffect(() => {
    if (!structure) return;
    const d: Draft = {
      title: structure.template?.title || "",
      description: structure.template?.description || "",
      cats: (structure.categories || []).map((c: any) => ({
        key: newKey(), name: c.name,
        subs: c.subcategories.map((s: any) => ({ key: newKey(), name: s.name, gls: s.gls.map((g: any) => g.glMasterId) })),
      })),
    };
    setDraft(d);
    setSaved(JSON.stringify(toPayload(d)));
    setErrors([]);
  }, [structure]);

  const dirty = !readOnly && !!saved && JSON.stringify(toPayload(draft)) !== saved;
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // GL labels: active GLs of the community plus any already in the structure (may be inactive now)
  const glOptions: any[] = useMemo(() => (glData?.data || []).filter((g: any) => g.active !== false), [glData]);
  const glById = useMemo(() => {
    const m = new Map<number, any>();
    (glData?.data || []).forEach((g: any) => m.set(g.glMasterId, g));
    (structure?.categories || []).forEach((c: any) => c.subcategories.forEach((s: any) => s.gls.forEach((g: any) => !m.has(g.glMasterId) && m.set(g.glMasterId, g))));
    return m;
  }, [glData, structure]);
  const usage = useMemo(() => {
    const m = new Map<number, { subKey: string; where: string }>();
    draft.cats.forEach((c) => c.subs.forEach((s) => s.gls.forEach((id) => m.set(id, { subKey: s.key, where: `${c.name || "Category"} › ${s.name || "Subcategory"}` }))));
    return m;
  }, [draft]);
  const unassigned = glOptions.filter((g) => !usage.has(g.glMasterId));
  const subCount = draft.cats.reduce((n, c) => n + c.subs.length, 0);

  // ---- edits ----
  const update = (fn: (d: Draft) => Draft) => setDraft((d) => fn(d));
  const setCats = (fn: (cats: Cat[]) => Cat[]) => update((d) => ({ ...d, cats: fn(d.cats) }));
  const setCat = (key: string, fn: (c: Cat) => Cat) => setCats((cats) => cats.map((c) => (c.key === key ? fn(c) : c)));
  const setSub = (catKey: string, subKey: string, fn: (s: Sub) => Sub) =>
    setCat(catKey, (c) => ({ ...c, subs: c.subs.map((s) => (s.key === subKey ? fn(s) : s)) }));
  const addCategory = () => {
    const cat = { key: newKey(), name: "", subs: [{ key: newKey(), name: "", gls: [] }] };
    setCats((cats) => [...cats, cat]);
  };
  const addSub = (catKey: string) => setCat(catKey, (c) => ({ ...c, subs: [...c.subs, { key: newKey(), name: "", gls: [] }] }));
  const toggle = (key: string) => setCollapsed((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const allCollapsed = draft.cats.length > 0 && draft.cats.every((c) => collapsed.has(c.key));

  // ---- validation (mirrors the backend so problems show before saving) ----
  const validate = (forPublish: boolean) => {
    const errs: string[] = [];
    if (!draft.title.trim()) errs.push("Financial Version Name is required.");
    const catNames = new Set<string>();
    draft.cats.forEach((c, ci) => {
      const cn = c.name.trim();
      if (!cn) errs.push(`Category ${ci + 1} needs a name.`);
      else if (catNames.has(cn.toLowerCase())) errs.push(`Category "${cn}" is used more than once.`);
      catNames.add(cn.toLowerCase());
      const subNames = new Set<string>();
      if (forPublish && c.subs.length === 0) errs.push(`Category "${cn || ci + 1}" has no subcategories.`);
      c.subs.forEach((s, si) => {
        const sn = s.name.trim();
        if (!sn) errs.push(`Subcategory ${si + 1} in "${cn || `Category ${ci + 1}`}" needs a name.`);
        else if (subNames.has(sn.toLowerCase())) errs.push(`Subcategory "${sn}" is used more than once in "${cn}".`);
        subNames.add(sn.toLowerCase());
        if (forPublish && s.gls.length === 0) errs.push(`Subcategory "${sn || si + 1}" in "${cn}" has no GL accounts.`);
      });
    });
    if (forPublish && draft.cats.length === 0) errs.push("Add at least one category before publishing.");
    setErrors(errs);
    return errs.length === 0;
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["financial-template-structure", financialTemplateId] });
    queryClient.invalidateQueries({ queryKey: ["financial-templates"] });
  };
  const { mutateAsync: saveAsync, isPending: isSaving } = useSaveFinancialTemplateStructureMutation();
  const { mutateAsync: publishAsync, isPending: isPublishing } = usePublishFinancialTemplateMutation();
  const { mutate: copy, isPending: isCopying } = useCopyFinancialTemplateMutation({
    onError: (e) => toast.error(errorMessage(e)),
    onSuccess: (res: any) => {
      const v = res?.data?.data;
      toast.success(`Version ${v?.versionNo} created as a draft`);
      queryClient.invalidateQueries({ queryKey: ["financial-templates"] });
      navigate(`../${v?.financialTemplateId}`, { relative: "path" });
    },
  });

  const save = async () => {
    if (!validate(false)) return false;
    try {
      await saveAsync({ financialTemplateId: financialTemplateId!, data: toPayload(draft) });
      setSaved(JSON.stringify(toPayload(draft)));
      refresh();
      return true;
    } catch (e) {
      setErrors([errorMessage(e)]);
      return false;
    }
  };
  const onSaveDraft = async () => {
    if (await save()) toast.success("Draft saved");
  };
  const onPublish = () => {
    if (!validate(true)) return;
    setConfirm({
      title: "Publish this version?",
      text: `Version ${template?.versionNo} becomes read-only and can be used in reports. Later changes go into a new version.${unassigned.length ? ` ${unassigned.length} active GL account(s) are not assigned.` : ""}`,
      action: async () => {
        if (dirty && !(await save())) return;
        try {
          await publishAsync({ financialTemplateId: financialTemplateId! });
          toast.success("Version published");
          refresh();
        } catch (e) {
          setErrors([errorMessage(e)]);
        }
      },
    });
  };
  const back = () => navigate("..", { relative: "path" });
  const onCancel = () => (dirty
    ? setConfirm({ title: "Discard changes?", text: "Your unsaved changes will be lost.", danger: true, action: back })
    : back());

  if (isLoading || !structure) return <div className="p-6"><Loader /></div>;

  const busy = isSaving || isPublishing;
  return (
    <div className="p-4 md:p-6 pb-28 flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button type="button" variant="outline" size="icon" onClick={onCancel} aria-label="Back"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h2 className="text-lg md:text-xl font-semibold">
              {readOnly ? "Financial Version" : "Build Financial Version"}
              <span className="font-normal text-gray-500"> — {community?.communityId == template?.communityId ? community?.name : `Community ${template?.communityId}`}</span>
            </h2>
            <p className="text-xs text-gray-500">Financial Statement Versions / {template?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
          {readOnly
            ? <Button onClick={() => copy({ financialTemplateId: financialTemplateId! })} disabled={isCopying}><Copy className="w-4 h-4" /> New Version</Button>
            : <Button onClick={onSaveDraft} disabled={busy || !dirty}><Save className="w-4 h-4" /> Save Draft</Button>}
        </div>
      </div>

      {/* Version details + summary */}
      <div className="bg-white rounded-xl border shadow-sm p-4 md:p-5 flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-start">
          <div className="flex flex-col gap-1">
            <Label htmlFor="fv-title">Financial Version Name *</Label>
            <Input id="fv-title" value={draft.title} maxLength={150} disabled={readOnly}
              onChange={(e) => update((d) => ({ ...d, title: e.target.value }))} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="fv-desc">Description</Label>
            <Input id="fv-desc" value={draft.description} maxLength={255} disabled={readOnly}
              onChange={(e) => update((d) => ({ ...d, description: e.target.value }))} />
          </div>
          <div className="flex items-center gap-2 md:pt-6">
            <Badge variant="outline" className="rounded-full px-3">v{template?.versionNo}</Badge>
            <StatusBadge status={template?.status} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 border-t pt-3">
          <span>{draft.cats.length} categor{draft.cats.length === 1 ? "y" : "ies"}</span>
          <span>{subCount} subcategor{subCount === 1 ? "y" : "ies"}</span>
          <span><span className="font-medium text-gray-800">{usage.size}</span> of {glOptions.length} active GL accounts assigned</span>
          {unassigned.length > 0 && (
            <button type="button" className="text-primary text-xs underline" onClick={() => setShowUnassigned((v) => !v)}>
              {showUnassigned ? "Hide" : "Show"} {unassigned.length} unassigned
            </button>
          )}
        </div>
        {showUnassigned && unassigned.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {unassigned.map((g) => (
              <span key={g.glMasterId} className="rounded-full border bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600">
                {g.glAccountNumber} – {g.accountShortDesc}
              </span>
            ))}
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          <p className="flex items-center gap-2 font-medium"><AlertTriangle className="w-4 h-4" /> Please fix the following</p>
          <ul className="mt-1 list-disc pl-6">{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}
      {readOnly && (
        <p className="rounded-md border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
          Published versions are read-only. Use <b>New Version</b> to make changes; the copy keeps this order.
        </p>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">Drag <GripVertical className="inline w-3 h-3" /> or use the arrows to change the order. Reports follow this order.</p>
        <div className="flex gap-2">
          <Button variant="outline" disabled={draft.cats.length === 0}
            onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(draft.cats.map((c) => c.key)))}>
            {allCollapsed ? <><ChevronsUpDown className="w-4 h-4" /> Expand All</> : <><ChevronsDownUp className="w-4 h-4" /> Collapse All</>}
          </Button>
          {!readOnly && <Button onClick={addCategory}><Plus className="w-4 h-4" /> Add Category</Button>}
        </div>
      </div>

      {draft.cats.length === 0 && (
        <div className="rounded-xl border border-dashed bg-white p-8 text-center text-sm text-gray-500">
          No categories yet. {!readOnly && <>Start with <b>Add Category</b> (e.g. Income, Operating Expenses).</>}
        </div>
      )}

      {/* Categories */}
      {draft.cats.map((cat, ci) => {
        const isCollapsed = collapsed.has(cat.key);
        const glCount = cat.subs.reduce((n, s) => n + s.gls.length, 0);
        const moveCat = (from: number, to: number) => setCats((cats) => move(cats, from, to));
        return (
          <div key={cat.key} {...bind("cat", ci, moveCat, !readOnly)}
            className={cn("rounded-xl border bg-white shadow-sm", isOver("cat", ci) && "ring-2 ring-primary/50")}>
            <div className="flex flex-wrap items-center gap-2 px-3 py-3 md:px-4">
              {!readOnly && <GripVertical className="w-4 h-4 cursor-grab text-gray-400" />}
              {!readOnly && <MoveButtons index={ci} length={draft.cats.length} label="category" onMove={(to) => moveCat(ci, to)} />}
              <button type="button" onClick={() => toggle(cat.key)} aria-label={isCollapsed ? "Expand" : "Collapse"}>
                {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <span className="text-xs font-semibold text-primary">CATEGORY {String(ci + 1).padStart(2, "0")}</span>
              <Input value={cat.name} disabled={readOnly} placeholder="Category name" maxLength={150}
                className="h-9 w-full sm:w-72 bg-white" onChange={(e) => setCat(cat.key, (c) => ({ ...c, name: e.target.value }))} />
              <div className="ml-auto flex items-center gap-2">
                <Badge variant="outline" className="rounded-full bg-blue-50 text-blue-700 border-blue-200">{cat.subs.length} subcategor{cat.subs.length === 1 ? "y" : "ies"}</Badge>
                <Badge variant="outline" className="rounded-full bg-blue-50 text-blue-700 border-blue-200">{glCount} GL{glCount === 1 ? "" : "s"}</Badge>
                {!readOnly && (
                  <button type="button" className="text-red-500 hover:text-red-700" aria-label="Delete category"
                    onClick={() => (cat.subs.some((s) => s.name || s.gls.length) || cat.name
                      ? setConfirm({ title: "Delete category?", text: `"${cat.name || `Category ${ci + 1}`}" and its ${cat.subs.length} subcategories will be removed when you save.`, danger: true,
                          action: () => setCats((cats) => cats.filter((c) => c.key !== cat.key)) })
                      : setCats((cats) => cats.filter((c) => c.key !== cat.key)))}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {!isCollapsed && (
              <div className="border-t px-3 pb-3 md:px-4">
                <div className="hidden md:grid grid-cols-[auto_minmax(180px,1fr)_2fr_auto] gap-3 px-2 py-2 text-xs font-medium text-gray-500">
                  <span className="w-10" /><span>Subcategory Name</span><span>Assigned GL Accounts (in report order)</span><span className="w-6" />
                </div>
                {cat.subs.map((sub, si) => {
                  const moveSub = (from: number, to: number) => setCat(cat.key, (c) => ({ ...c, subs: move(c.subs, from, to) }));
                  const subScope = `sub:${cat.key}`;
                  return (
                    <div key={sub.key} {...bind(subScope, si, moveSub, !readOnly)}
                      className={cn("grid grid-cols-1 md:grid-cols-[auto_minmax(180px,1fr)_2fr_auto] gap-3 rounded-lg border-b px-2 py-3 last:border-b-0",
                        isOver(subScope, si) && "bg-primary/5")}>
                      <div className="flex items-start gap-1 pt-2">
                        {!readOnly && <GripVertical className="w-4 h-4 cursor-grab text-gray-400" />}
                        {!readOnly && <MoveButtons index={si} length={cat.subs.length} label="subcategory" onMove={(to) => moveSub(si, to)} />}
                      </div>
                      <Input value={sub.name} disabled={readOnly} placeholder="Subcategory name" maxLength={150} className="h-9 bg-white"
                        onChange={(e) => setSub(cat.key, sub.key, (s) => ({ ...s, name: e.target.value }))} />
                      <div className="flex flex-col gap-2">
                        {sub.gls.length > 0 && (
                          <ol className="flex flex-col gap-1">
                            {sub.gls.map((id, gi) => {
                              const g = glById.get(id);
                              const glScope = `gl:${sub.key}`;
                              const moveGl = (from: number, to: number) => setSub(cat.key, sub.key, (s) => ({ ...s, gls: move(s.gls, from, to) }));
                              return (
                                <li key={id} {...bind(glScope, gi, moveGl, !readOnly)}
                                  className={cn("flex items-center gap-2 rounded-md border bg-gray-50 px-2 py-1 text-sm", isOver(glScope, gi) && "ring-2 ring-primary/40")}>
                                  {!readOnly && <GripVertical className="w-3.5 h-3.5 cursor-grab text-gray-400" />}
                                  <span className="w-6 text-right text-xs text-gray-400">{gi + 1}.</span>
                                  <span className="flex-1"><span className="font-medium">{g?.glAccountNumber ?? id}</span> – {g?.accountShortDesc ?? ""}
                                    {g && g.active === false && <span className="ml-2 text-xs text-amber-600">(inactive GL)</span>}</span>
                                  {!readOnly && <MoveButtons index={gi} length={sub.gls.length} label="GL" onMove={(to) => moveGl(gi, to)} />}
                                  {!readOnly && (
                                    <button type="button" className="text-gray-400 hover:text-red-600" aria-label="Remove GL"
                                      onClick={() => setSub(cat.key, sub.key, (s) => ({ ...s, gls: s.gls.filter((x) => x !== id) }))}>
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </li>
                              );
                            })}
                          </ol>
                        )}
                        {!readOnly && (
                          <GlPicker glOptions={glOptions} usage={usage} ownKey={sub.key}
                            onAdd={(id) => setSub(cat.key, sub.key, (s) => ({ ...s, gls: [...s.gls, id] }))} />
                        )}
                        {readOnly && sub.gls.length === 0 && <span className="text-sm text-gray-400">No GL accounts</span>}
                      </div>
                      <div className="pt-2">
                        {!readOnly && (
                          <button type="button" className="text-red-500 hover:text-red-700" aria-label="Delete subcategory"
                            onClick={() => setCat(cat.key, (c) => ({ ...c, subs: c.subs.filter((s) => s.key !== sub.key) }))}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {!readOnly && (
                  <Button type="button" variant="outline" className="mt-3 border-dashed" onClick={() => addSub(cat.key)}>
                    <Plus className="w-4 h-4" /> Add Subcategory
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Sticky footer */}
      {!readOnly && (
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-[1600px] items-center justify-end gap-2 px-4 py-3 md:px-6">
            <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button variant="outline" onClick={onSaveDraft} disabled={busy || !dirty}><Save className="w-4 h-4" /> Save Draft</Button>
            <Button onClick={onPublish} disabled={busy}><Send className="w-4 h-4" /> Publish Version</Button>
          </div>
        </div>
      )}

      <Dialog open={!!confirm} setOpen={(o: boolean) => !o && setConfirm(null)} hideFooter title={confirm?.title}>
        <div className="flex flex-col gap-4">
          <p className="text-sm">{confirm?.text}</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant={confirm?.danger ? "destructive" : "default"}
              onClick={() => { const a = confirm?.action; setConfirm(null); a?.(); }}>
              {confirm?.danger ? "Yes, continue" : "Publish"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default FinancialVersionBuilder;
